// THE REBUILD'S SWITCHES (UI rebuild, 26 Oct 2026). Astra's redesign lands batch by batch, each batch switched off
// until the operator says it ships — like CUSTODY_IN_CONSOLE, a build-time constant, so a switched-off batch costs
// the live console nothing: with no batch on, every screen, colour and nav item is exactly what main draws today.
//
//   SHIPPED            the batches released to everyone (his word, quoted in the commit that adds one)
//   PREVIEWABLE        the batches in the live bundle that the OWNER may switch on for himself, on one device, to see
//                      them on his real books (the Command Centre's plan, 26 Oct 2026). Off by default; nobody else's
//                      console changes. The switch lives at /ui-preview (screens/v2/UiPreview.tsx), shown only to the
//                      owner (/api/me canApprovePayouts); it writes one localStorage key and reloads.
//   VITE_UI_V2="1,2"   a LOCAL build's override, for the mock renders (mini-app/bench/run.sh) — never set on a deploy
//   VITE_UI_THEME      the rebuilt console's theme for a render: 'dark' (today's, the default while his answer on
//                      light vs dark is open) or 'light' (Astra's prototype)
//
// Batch 1 is the shared system: the palette, the shell (sidebar, top bar, phone tabs, More, New entry) and the kit's
// look. Switching it on repaints every screen, rebuilt or not — a release reviewed on its own renders.
export type UiBatch = 1 | 2 | 3 | 4 | 5;

const SHIPPED: readonly UiBatch[] = [];

function parse(raw: string | undefined): readonly UiBatch[] | null {
  if (raw === undefined || raw.trim() === '') return null;
  return raw.split(',').map((s) => Number(s.trim())).filter((n): n is UiBatch => n >= 1 && n <= 5);
}

// Batches 1–3 (the shared system, daily work, the entry forms and Settle later): the operator's "Go deploy", choosing
// "New console (your switch)", relayed verbatim by the Command Centre, 27 Oct 2026. Batches 4–5 (KYT and Energy; the
// reports, Close, Legacy, Names, Inventory, Exchanges, Onboarding, Wallets, Cheque progress, Sign in): his "Ok deploy them
// new UI & rebuild & console", relayed verbatim by the Command Centre, 27 Oct 2026 — still the owner's switch only.
const PREVIEWABLE: readonly UiBatch[] = [1, 2, 3, 4, 5];

/** The owner's own opt-in on this device (/ui-preview). A view preference, read once at start — it unlocks nothing the
 *  API does not already allow, so a value set by hand changes only that browser's look. */
export const PREVIEW_KEY = 'ui.v2.preview';
function optedIn(): boolean {
  try { return localStorage.getItem(PREVIEW_KEY) === '1'; } catch { return false; }
}

/** May this build offer the owner the preview at all? (Nothing to preview until a batch is listed in PREVIEWABLE.) */
export const PREVIEW_OFFERED: boolean = PREVIEWABLE.length > 0;
/** Is the owner's preview on, on this device? */
export const PREVIEW_ON: boolean = PREVIEW_OFFERED && optedIn();

const ON: ReadonlySet<UiBatch> = new Set(parse(import.meta.env.VITE_UI_V2) ?? [...SHIPPED, ...(PREVIEW_ON ? PREVIEWABLE : [])]);

/** Is this batch of the rebuild switched on in this build? */
export const uiV2 = (batch: UiBatch): boolean => ON.has(batch);

export type UiTheme = 'dark' | 'light';
export const UI_THEME: UiTheme = import.meta.env.VITE_UI_THEME === 'light' ? 'light' : 'dark';

/** The phone's tab bar while the operator's question C4 is open (lib/routes.ts PHONE_TAB_SETS): `astra` by default,
 *  `daily` for main's seven daily tabs. VITE_UI_TABS overrides it for a local render of the other answer. */
export type PhoneTabsSet = 'astra' | 'daily';
const TABS_SHIPPED: PhoneTabsSet = 'astra';
export const PHONE_TABS_SET: PhoneTabsSet =
  import.meta.env.VITE_UI_TABS === 'daily' ? 'daily' : import.meta.env.VITE_UI_TABS === 'astra' ? 'astra' : TABS_SHIPPED;
